import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { defaultFont, defaultFrame, BaseAttributeModel, SingleAttributeModel, MultiAttributeModel, TableAttributeModel, ImageAttributeModel, NondispleyAttributeModel } from './cardAttributeModelIndex';
import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type SingleAttributeTemplate = { cardTemplateId: string; } & SingleAttributeModel;
export type MultiAttributeTemplate = { cardTemplateId: string; } & MultiAttributeModel;
export type TableAttributeTemplate = { cardTemplateId: string; } & TableAttributeModel;
export type ImageAttributeTemplate = { cardTemplateId: string; } & ImageAttributeModel;
export type NondispleyAttributeTemplate = { cardTemplateId: string; } & NondispleyAttributeModel;

export type TemplateAttributeHavingFont = SingleAttributeTemplate | MultiAttributeTemplate | TableAttributeTemplate;

export type CardTemplateAttributeDb = SingleAttributeTemplate | MultiAttributeTemplate | TableAttributeTemplate | ImageAttributeTemplate | NondispleyAttributeTemplate;

const initialBase: BaseAttributeModel = {
  languageId: 'ja',
  name: '',
  hint: '',
  frame: defaultFrame,
  order: 0,
  x: 0,
  y: 0,
  z: 0,
  width: 100,
  height: 100,
  rotation: 0,
  description: {
    display: false,
    content: ''
  },
  background: {
    display: false,
    color: '#ffffff'
  },
  goodsId: undefined,
  version: undefined
};
type CardTemplateAttributeInitialType = CardTemplateAttributeDb[]
const initial: CardTemplateAttributeInitialType = [
  {
    ...initialBase,
    cardAttributeModel: 'single',
    font: defaultFont,
    defaultContent: '',
    cardTemplateId: ''
  },
  {
    ...initialBase,
    cardAttributeModel: 'multi',
    font: defaultFont,
    defaultContent: '',
    isMarkdown: false,
    cardTemplateId: ''
  },
  {
    ...initialBase,
    cardAttributeModel: 'table',
    font: defaultFont,
    columnNames: {},
    cardTemplateId: ''
  },
  {
    ...initialBase,
    cardAttributeModel: 'image',
    defaultImage: {
      imageId: '',
      originWidth: 0,
      originHeight: 0,
      trimXFrom: 0,
      trimYFrom: 0,
      trimXTo: 0,
      trimYTo: 0
    },
    cardTemplateId: ''
  },
  {
    ...initialBase,
    cardAttributeModel: 'nondisplay',
    defaultContent: '',
    cardTemplateId: ''
  },
]

export type CardTemplateAttributeDbHashType = {
  [key in string]: CardTemplateAttributeDb;
}

export type CardTemplateAttributeAddPayload = {
  id?: string;
} & CardTemplateAttributeDb;

export type CardTemplateAttributeUpdatePayload = {
  id: string;
} & (
  {[P in keyof SingleAttributeTemplate]?: SingleAttributeTemplate[P]} |
  {[P in keyof MultiAttributeTemplate]?: MultiAttributeTemplate[P]} |
  {[P in keyof TableAttributeTemplate]?: TableAttributeTemplate[P]} |
  {[P in keyof ImageAttributeTemplate]?: ImageAttributeTemplate[P]} |
  {[P in keyof NondispleyAttributeTemplate]?: NondispleyAttributeTemplate[P]}
)

export type CardTemplateAttributeDbState = {
  cardTemplateAttributes: CardTemplateAttributeDbHashType
}

const isCardTemplateAttributeDb = (obj: any): obj is CardTemplateAttributeDb => {
  return 'cardAttributeModel' in obj && 'cardTemplateId' in obj;
}
export const isImageAttributeTemplate = (obj: any): obj is ImageAttributeTemplate => {
  return 'cardAttributeModel' in obj && obj.cardAttributeModel === 'image' && 'cardTemplateId' in obj;
}
export const isNondisplayAttributeTemplate = (obj: any): obj is NondispleyAttributeTemplate => {
  return typeof obj === 'object' && !Array.isArray(obj) && 'cardAttributeModel' in obj && obj.cardAttributeModel === 'nondisplay' && 'cardTemplateId' in obj;
}
const isSameType = <T, _>(a: T, b: any): b is T => {
  return isCardTemplateAttributeDb(a) && a.cardAttributeModel === b?.cardAttributeModel;
}

export const generateCardTemplateAttributeId = (): string => generateId('cta');
let initialState: CardTemplateAttributeDbState = { cardTemplateAttributes: {} };
const key = 'cardTemplateAttributes';

const cardTemplateAttributeDbSlice = createSlice({
  name: 'cardTemplateAttributeDb',
  initialState,
  reducers: {
    initializeCardTemplateAttributes(state: CardTemplateAttributeDbState) {
      state.cardTemplateAttributes = {};
    },
    addCardTemplateAttribute(state: CardTemplateAttributeDbState, action: PayloadAction<CardTemplateAttributeAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateCardTemplateAttributeId();
      state.cardTemplateAttributes[id] = obj;
      saveData(key, id, obj);
    },
    updateCardTemplateAttribute(state: CardTemplateAttributeDbState, action: PayloadAction<CardTemplateAttributeUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.cardTemplateAttributes[id] === undefined) {
        console.log(`*** CardTemplateAttribute not found: ${id}`);
      } else if (isSameType(state.cardTemplateAttributes[id], obj)) {
        const newData = { ...state.cardTemplateAttributes[id], ...obj };
        state.cardTemplateAttributes[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteCardTemplateAttribute(state: CardTemplateAttributeDbState, action: PayloadAction<CardTemplateAttributeUpdatePayload>) {
      const id = action.payload.id;
      delete state.cardTemplateAttributes[id];
      deleteData(key, id);
    },
    saveCardTemplateAttributes(state: CardTemplateAttributeDbState, action: PayloadAction<void>) {
      saveAllData(key, state.cardTemplateAttributes);
    },
    signUpCardTemplateAttributes(state: CardTemplateAttributeDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.cardTemplateAttributes, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: CardTemplateAttributeDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.cardTemplateAttributes = updatedValues;
      if (changed) saveAllData(key, state.cardTemplateAttributes);
    })
  }
})

export const { initializeCardTemplateAttributes, addCardTemplateAttribute, updateCardTemplateAttribute, deleteCardTemplateAttribute, saveCardTemplateAttributes, signUpCardTemplateAttributes } = cardTemplateAttributeDbSlice.actions;
export default cardTemplateAttributeDbSlice.reducer;
